// src/store/thunks/branchInstallmentThunks.js
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// ========== CONFIGURATION MANAGEMENT ==========

/**
 * Initialize installment configuration for a branch
 * POST /api/branches/:branchCode/installment-config/initialize
 */
export const initializeInstallmentConfigThunk = createAsyncThunk(
  "branchInstallment/initializeConfig",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-branches-installment/${branchCode}/installment-config/initialize`
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get installment configuration for a branch
 * GET /api/branches/:branchCode/installment-config
 */
export const getInstallmentConfigThunk = createAsyncThunk(
  "branchInstallment/getConfig",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin-branches-installment/${branchCode}/installment-config`
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Toggle installments on/off for a branch
 * PATCH /api/branches/:branchCode/installment-config/toggle
 */
export const toggleInstallmentsThunk = createAsyncThunk(
  "branchInstallment/toggle",
  async ({ branchCode, enabled }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-branches-installment/${branchCode}/installment-config/toggle`,
        { enabled }
      );
      return { branchCode, enabled, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update interest configuration
 * PATCH /api/branches/:branchCode/installment-config/interest
 */
export const updateInterestConfigThunk = createAsyncThunk(
  "branchInstallment/updateInterestConfig",
  async ({ branchCode, interestConfig }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-branches-installment/${branchCode}/installment-config/interest`,
        interestConfig
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update payment rules
 * PATCH /api/branches/:branchCode/installment-config/payment-rules
 */
export const updatePaymentRulesThunk = createAsyncThunk(
  "branchInstallment/updatePaymentRules",
  async ({ branchCode, paymentRules }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-branches-installment/${branchCode}/installment-config/payment-rules`,
        paymentRules
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update pause policy
 * PATCH /api/branches/:branchCode/installment-config/pause-policy
 */
export const updatePausePolicyThunk = createAsyncThunk(
  "branchInstallment/updatePausePolicy",
  async ({ branchCode, pausePolicy }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-branches-installment/${branchCode}/installment-config/pause-policy`,
        pausePolicy
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update KYC requirements
 * PATCH /api/branches/:branchCode/installment-config/kyc-requirements
 */
export const updateKYCRequirementsThunk = createAsyncThunk(
  "branchInstallment/updateKYCRequirements",
  async ({ branchCode, kycRequirements }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        `/admin-branches-installment/${branchCode}/installment-config/kyc-requirements`,
        kycRequirements
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== INSTALLMENT PLANS ==========

/**
 * Get all installment plans for a branch
 * GET /api/branches/:branchCode/installment-plans
 */
export const getInstallmentPlansThunk = createAsyncThunk(
  "branchInstallment/getPlans",
  async ({ branchCode, activeOnly = false }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin-branches-installment/${branchCode}/installment-plans`,
        { params: { activeOnly } }
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Create new installment plan
 * POST /api/branches/:branchCode/installment-plans
 */
export const createInstallmentPlanThunk = createAsyncThunk(
  "branchInstallment/createPlan",
  async ({ branchCode, planData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin-branches-installment/${branchCode}/installment-plans`,
        planData
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      const errorDetails = error.response?.data?.details || [];
      return rejectWithValue({ message: errorMessage, details: errorDetails });
    }
  }
);

/**
 * Update installment plan
 * PUT /api/branches/:branchCode/installment-plans/:planId
 */
export const updateInstallmentPlanThunk = createAsyncThunk(
  "branchInstallment/updatePlan",
  async ({ branchCode, planId, updates }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/admin-branches-installment/${branchCode}/installment-plans/${planId}`,
        updates
      );
      return { branchCode, planId, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Delete/Deactivate installment plan
 * DELETE /api/branches/:branchCode/installment-plans/:planId
 */
export const deleteInstallmentPlanThunk = createAsyncThunk(
  "branchInstallment/deletePlan",
  async ({ branchCode, planId, hardDelete = false }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/admin-branches-installment/${branchCode}/installment-plans/${planId}`,
        { params: { hardDelete } }
      );
      return { branchCode, planId, hardDelete, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      const activeOrders = error.response?.data?.data?.activeOrders;
      return rejectWithValue({ message: errorMessage, activeOrders });
    }
  }
);

// ========== ANALYTICS ==========

/**
 * Get installment analytics for a branch
 * GET /api/branches/:branchCode/installment-analytics
 */
export const getInstallmentAnalyticsThunk = createAsyncThunk(
  "branchInstallment/getAnalytics",
  async ({ branchCode, fromDate, toDate }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin-branches-installment/${branchCode}/installment-analytics`,
        { params: { fromDate, toDate } }
      );
      return { branchCode, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== ACTIONS FOR STATE MANAGEMENT ==========

/**
 * Clear installment errors
 */
export const clearInstallmentError = createAction(
  "branchInstallment/clearError"
);

/**
 * Reset installment config
 */
export const resetInstallmentConfig = createAction(
  "branchInstallment/resetConfig"
);

/**
 * Set selected plan
 */
export const setSelectedPlan = createAction(
  "branchInstallment/setSelectedPlan"
);

/**
 * Clear selected plan
 */
export const clearSelectedPlan = createAction(
  "branchInstallment/clearSelectedPlan"
);

/**
 * Set editing mode for config
 */
export const setConfigEditMode = createAction(
  "branchInstallment/setConfigEditMode"
);

/**
 * Set editing mode for plan
 */
export const setPlanEditMode = createAction(
  "branchInstallment/setPlanEditMode"
);

/**
 * Clear analytics
 */
export const clearAnalytics = createAction("branchInstallment/clearAnalytics");
